﻿import { fetchRequest } from '@tk/utilities/tk.fetch';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';

interface CustomElementAccordionIcon {
    iconAdd: string;
    iconRemove: string;
}

export default class TKTabsAccordion extends TKCustomElementFactory {
    activeClassName: string;
    accordionHead?: HTMLDivElement;
    accordionBody?: HTMLDivElement;
    accordionContent?: HTMLDivElement;
    accordionIcon?: HTMLElement;
    triggerArea?: Element;
    accordionIcons: CustomElementAccordionIcon;
    isActive: boolean;
    isHeaderLink?: boolean;
    asyncUrl?: string;

    constructor() {
        super();

        this.accordionHead = this.querySelector('[data-tk-accordion-head]') || undefined;
        this.accordionBody = this.querySelector('[data-tk-accordion-body]') || undefined;
        this.accordionContent = this.querySelector('[data-tk-accordion-content]') || undefined;
        this.accordionIcon = this.querySelector('[data-tk-accordion-icon]') || undefined;
        this.accordionIcons = {
            iconAdd: this.getAttribute('data-tk-icon-open') || 'tk-icon-add',
            iconRemove: this.getAttribute('data-tk-icon-collapse') || 'tk-icon-remove',
        };
        this.isActive = this.hasAttribute('data-tk-accordion-is-active');
        this.isHeaderLink = this.accordionHead && this.accordionHead.hasAttribute('data-tk-accordion-link');
        this.activeClassName = this.getAttribute('data-tk-active-class-name') || 'tk-tabs-accordion--active';
        this.asyncUrl = this.getAttribute('data-content-url') || undefined;
    }

    connectedCallback(): void {
        if ((!this.accordionHead || !this.accordionBody || !this.accordionIcon) && !this.isHeaderLink)
            throw new Error('Accordion: Elements are missing!');

        this.setAccordionState(this.isActive);
        this.registerListener();
        this.openOnInit();
    }

    registerListener(): void {
        const eventHandler = this.toggleEvent.bind(this);
        this.pushListener({ event: 'click', element: this.accordionHead!, action: eventHandler });
    }

    toggleEvent(): void {
        this.toggleMarkupElements();
        if (this.asyncUrl && this.accordionContent?.innerHTML === '') {
            const htmlString = '<div class="kdmz-loading-icon"><div></div><div></div><div></div><div></div></div>';
            const loadingElement = document.createElement('div');
            loadingElement.innerHTML = htmlString;
            loadingElement.classList.add('flex', 'flex--justify-center', 'spacer-mb-4', 'spacer-mt-4');
            this.accordionContent.append(loadingElement);
            fetchRequest({
                requestURL: this.asyncUrl!,
                resolveHandler: this.handleResponseSuccess.bind(this, this.asyncUrl!),
            });
        }
    }

    openOnInit(): void {
        if (this.hasAttribute('data-open-on-init')) {
            this.toggleMarkupElements();
            this.accordionHead!.classList.add('tk-tabs-accordion--active');
            if (this.asyncUrl && this.accordionContent?.innerHTML === '') {
                const htmlString = '<div class="kdmz-loading-icon"><div></div><div></div><div></div><div></div></div>';
                const loadingElement = document.createElement('div');
                loadingElement.innerHTML = htmlString;
                loadingElement.classList.add('flex', 'flex--justify-center', 'spacer-mb-4', 'spacer-mt-4');
                this.accordionContent.append(loadingElement);
                fetchRequest({
                    requestURL: this.asyncUrl!,
                    resolveHandler: this.handleResponseSuccess.bind(this, this.asyncUrl!),
                });
            }
        }
    }

    handleResponseSuccess(url: string, response: TKResponse): void {
        if (!response || !response.success) return;
        const content = this.accordionContent;
        if (!content) return;
        content.innerHTML = response.dataAsHtml;
    }

    toggleMarkupElements(): void {
        if (this.closest('.tk-tabs-accordion__group')) {
            const currentState = this.classList.contains(this.activeClassName) as boolean;
            this.closeAllAccordions();
            if (currentState) {
                this.classList.remove(this.activeClassName);
                this.accordionIcon?.classList.add(this.accordionIcons.iconAdd);
                this.accordionIcon?.classList.remove(this.accordionIcons.iconRemove);
            } else {
                this.classList.add(this.activeClassName);
                this.accordionIcon?.classList.remove(this.accordionIcons.iconAdd);
                this.accordionIcon?.classList.add(this.accordionIcons.iconRemove);
            }
        } else {
            this.classList.toggle(this.activeClassName);
            this.accordionIcon?.classList.toggle(this.accordionIcons.iconAdd);
            this.accordionIcon?.classList.toggle(this.accordionIcons.iconRemove);
        }
        const group = this.closest('.tk-tabs-accordion__group') as HTMLElement;
        const allOnInitAccordions = group.querySelectorAll('[data-open-on-init]') as NodeListOf<HTMLElement>;
        allOnInitAccordions.forEach((accordion) => {
            accordion.classList.add('tk-tabs-accordion--active');
        });
    }

    setAccordionState(isActive: boolean): void {
        if (isActive) {
            this.classList.toggle(this.activeClassName);
            this.accordionIcon?.classList.add(this.accordionIcons.iconRemove);
        } else {
            this.accordionIcon?.classList.add(this.accordionIcons.iconAdd);
        }
    }

    closeAllAccordions() {
        const group = this.closest('.tk-tabs-accordion__group') as HTMLElement;
        const allAccordions = group.querySelectorAll('.tk-tabs-accordion--active') as NodeListOf<HTMLElement>;
        allAccordions.forEach((accordion) => {
            accordion.classList.remove('tk-tabs-accordion--active');
            accordion.querySelector('[data-tk-accordion-icon]')?.classList.toggle(this.accordionIcons.iconAdd);
            accordion.querySelector('[data-tk-accordion-icon]')?.classList.toggle(this.accordionIcons.iconRemove);
        });
    }
}
